import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import withStyles from '@mui/styles/withStyles';
import { defaultKnowledgeGraphMeta } from 'store/modules/graph/knowledgeGraphMeta';
import TextField from 'studio/components/TextField';
import TextArea from 'studio/components/TextArea';
import Select from 'studio/components/Select';
import { getErrorMessage } from 'utils/forms';
import { useLoader } from 'components/loaders/FadeLoader';
import styles from './styles';
import Tabs from 'studio/components/Tabs/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from 'studio/components/Link';
import { KgPermissions } from '../../constants';
import DeleteIcon from '@mui/icons-material/Delete';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import { LLM_QUERY_ACCESS_SIGNUP } from 'forms/text';
import { FormCheckbox } from 'components/v3';

const KnowledgeGraphForm = ({
  classes,
  languages,
  knowledgeGraph,
  orgSettings,
  updateKnowledgeGraph,
  deleteKnowledgeGraph,
  fetchKnowledgeGraphs,
  createDevKnowledgeGraph
}) => {
  const { loading } = useLoader();

  const defaultValues = useMemo(
    () => ({
      // needed since the multi-select requires an array
      languages: [],
      ...(orgSettings ? { isLlmDataAccessAllowed: false } : {})
    }),
    [orgSettings]
  );

  // the validation rules for the form
  const validationRules = yup.object().shape(
    Object.assign(
      {},
      // add a validation rule for the name field for each available language
      ...languages.map(language => ({
        [`name-${language.value}`]: yup.string().when('languages', {
          // if the selected languages contain this language xx-XX...
          is: value => value.includes(language.value),
          // ...then make the name-xx-XX field required
          then: () => yup.string().required('The name is required').max(28, 'The name must not exceed 28 characters')
        })
      }))
    )
  );

  const { handleSubmit, control, errors, reset, watch, formState } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(validationRules)
  });

  // the default language of the knowledge graph
  const defaultLanguage = useMemo(() => knowledgeGraph.data.defaultLanguageId, [knowledgeGraph]);
  // sort the available languages: first the knowledge graph's default language, then the remaining ones alphabetically
  const sortedLanguages = useMemo(
    () => languages.sort((l1, l2) => (l1.value === defaultLanguage ? -1 : l1.value.localeCompare(l2.value))),
    [languages]
  );
  const kgPermissions = useMemo(() => knowledgeGraph?.data.kgPermissions || [], [knowledgeGraph]);
  // the selected languages
  const selectedLanguages = watch('languages') || [];
  // the selected languages in the above sorted order
  const selectedSortedLanguages = sortedLanguages
    .map(language => language.value)
    .filter(language => selectedLanguages.includes(language));
  // the index of the selected language tab
  const [languageTabIndex, setLanguageTabIndex] = useState(0);

  // the initial data for the form
  const initialData = useMemo(
    () =>
      Object.assign(
        {
          languages: knowledgeGraph.data.languages,
          isLlmDataAccessAllowed: knowledgeGraph.data.isLlmDataAccessAllowed
        },
        // flatten the knowledge graph 'info' data into fields with language suffix (e.g. 'name-en-US')
        ...Object.keys(knowledgeGraph.data.info).map(language => ({
          [`name-${language}`]: knowledgeGraph.data.info[language].name,
          [`description-${language}`]: knowledgeGraph.data.info[language].description,
          [`supportUrl-${language}`]: knowledgeGraph.data.info[language].supportUrl,
          [`documentationUrl-${language}`]: knowledgeGraph.data.info[language].documentationUrl
        }))
      ),
    [knowledgeGraph]
  );

  // resets the form initially
  useEffect(() => {
    reset(initialData);
  }, [knowledgeGraph, reset]);

  // adjusts the selected language tab, if necessary, when a language gets deselected
  useEffect(() => {
    // make sure the index is between '0' and 'length - 1'
    const adjustedTabIndex = Math.max(Math.min(languageTabIndex, selectedSortedLanguages.length - 1), 0);
    setLanguageTabIndex(adjustedTabIndex);
  }, [selectedSortedLanguages]);

  // sets the selected language tab to one with an error, if any, after the form has been submitted
  useEffect(() => {
    const errorTabIndex = selectedSortedLanguages.findIndex(language => hasError(language));
    if (errorTabIndex >= 0) {
      setLanguageTabIndex(errorTabIndex);
    }
  }, [formState.submitCount]);

  // returns the name of a language
  const getName = useCallback(language => languages.find(l => l.value === language)?.name, [languages]);

  // checks whether a field for the specified language has an error
  const hasError = useCallback(language => !!Object.keys(errors).find(name => name.endsWith(`-${language}`)), [errors]);

  // updates the knowledge graph when the form is submitted
  const onSubmit = useCallback(
    async data => {
      // structure the data into an object for each selected language
      const info = Object.assign(
        {},
        ...data.languages.map(language => ({
          [language]: {
            name: data[`name-${language}`],
            description: data[`description-${language}`] || null,
            supportUrl: data[`supportUrl-${language}`] || null,
            documentationUrl: data[`documentationUrl-${language}`] || null
          }
        }))
      );
      const body = {
        ...knowledgeGraph.data,
        languages: data.languages,
        info,
        isLlmDataAccessAllowed: data.isLlmDataAccessAllowed
      };
      const result = await updateKnowledgeGraph(body);
      if (result.success) {
        // retrieve the knowledge graphs after a successful update
        fetchKnowledgeGraphs();
      }
    },
    [knowledgeGraph, updateKnowledgeGraph, fetchKnowledgeGraphs]
  );

  const hasNoMetadataWritePermission = loading || !kgPermissions.includes(KgPermissions.KgMetadataWrite);

  const devKgTooltip =
    (hasNoMetadataWritePermission && '') ||
    (!kgPermissions.includes(KgPermissions.Write) &&
      'You can only create a development KG for this KG if you have write permissions.') ||
    (knowledgeGraph.data.developmentKgForId &&
      "This knowledge graph is already a development copy of the KG '" +
        knowledgeGraph.data.developmentKgForName +
        "'.") ||
    'Create a branch (an independent copy) of this knowledge graph for development. It will be connected to this knowledge graph and has operations to sync changes.';

  return (
    !!knowledgeGraph && (
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)} className={classes.root}>
        {knowledgeGraph.data.developmentKgForId && (
          <Typography variant="body1" gutterBottom>
            This knowledge graph is a development copy of the knowledge graph{' '}
            <Link kgId={knowledgeGraph.data.developmentKgForId}>{knowledgeGraph.data.developmentKgForName}</Link>.
          </Typography>
        )}
        <TextField
          name="id"
          label="ID"
          description="The ID of the knowledge graph"
          value={knowledgeGraph.data.id}
          disabled={true}
        />

        <Controller
          as={Select}
          name="languages"
          values={sortedLanguages.map(item => [item.value, item.value === defaultLanguage])}
          labels={sortedLanguages.map(item => item.name + (item.value === defaultLanguage ? ' (default)' : ''))}
          control={control}
          label="Languages"
          description="The supported languages of the knowledge graph"
          multiple
          disabled={hasNoMetadataWritePermission}
        />

        <Tabs
          value={languageTabIndex}
          onChange={(_, index) => setLanguageTabIndex(index)}
          className={selectedSortedLanguages.find(language => hasError(language)) ? 'error' : ''}
        >
          {selectedSortedLanguages.map(language => (
            <Tab key={language} label={getName(language)} className={hasError(language) ? 'error' : ''} />
          ))}
        </Tabs>

        {selectedSortedLanguages.map((language, index) => (
          <div key={language} id={language} hidden={index !== languageTabIndex} className={classes.tabContent}>
            <Controller
              as={TextField}
              name={`name-${language}`}
              control={control}
              label={`Name (${getName(language)})`}
              error={Boolean(errors[`name-${language}`])}
              description={getErrorMessage(errors[`name-${language}`]) || 'The name of the knowledge graph'}
              disabled={hasNoMetadataWritePermission}
              defaultValue={null}
            />

            <Controller
              as={TextArea}
              name={`description-${language}`}
              control={control}
              label={`Description (${getName(language)})`}
              description="The description of the knowledge graph"
              disabled={hasNoMetadataWritePermission}
              defaultValue={null}
            />

            <Controller
              as={TextField}
              name={`supportUrl-${language}`}
              control={control}
              label={`Support URL (${getName(language)})`}
              placeholder={defaultKnowledgeGraphMeta.meta.htmlSupport}
              description="The URL (https or mailto) to the support channel for the knowledge graph"
              disabled={hasNoMetadataWritePermission}
              defaultValue={null}
            />

            <Controller
              as={TextField}
              name={`documentationUrl-${language}`}
              control={control}
              label={`Documentation URL (${getName(language)})`}
              placeholder={defaultKnowledgeGraphMeta.meta.linkToDocumentation}
              description="The URL to the documentation of the knowledge graph"
              disabled={hasNoMetadataWritePermission}
              defaultValue={null}
            />
            {orgSettings?.data?.isLlmDataAccessAllowed && (
              <Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Allow third-party LLMs (e.g., Anthropic, Google, OpenAI) to access database query results associated
                  with this knowledge graph, which enables capabilities like result summarization, better follow-up
                  questions, and more natural explanations.
                </Typography>

                <Controller
                  as={FormCheckbox}
                  name="isLlmDataAccessAllowed"
                  content={LLM_QUERY_ACCESS_SIGNUP.LABEL}
                  control={control}
                  disabled={hasNoMetadataWritePermission}
                  data-test="llm-data-access-switch"
                  checkboxClasses={{ default: { paddingLeft: '0 !important' } }}
                />
              </Box>
            )}
          </div>
        ))}

        <Box className={classes.actions}>
          <Button
            type="button"
            color="secondary"
            disabled={!kgPermissions.includes(KgPermissions.KgDelete)}
            onClick={deleteKnowledgeGraph}
            startIcon={<DeleteIcon />}
          >
            {knowledgeGraph?.data?.developmentKgForId ? 'Delete Branch' : 'Delete Knowledge Graph'}
          </Button>
          <Tooltip title={devKgTooltip} placement="top">
            <span>
              <Button
                type="button"
                onClick={createDevKnowledgeGraph}
                disabled={!kgPermissions.includes(KgPermissions.Write) || !!knowledgeGraph?.data?.developmentKgForId}
                startIcon={<CallSplitIcon />}
              >
                Create Branch
              </Button>
            </span>
          </Tooltip>
          <Button
            className={classes.right}
            type="submit"
            variant="contained"
            color="primary"
            disabled={hasNoMetadataWritePermission}
          >
            Save
          </Button>
          <Button className={classes.right} onClick={() => reset(initialData)} disabled={hasNoMetadataWritePermission}>
            Reset
          </Button>
        </Box>
      </form>
    )
  );
};

export default memo(withStyles(styles)(KnowledgeGraphForm));

KnowledgeGraphForm.propTypes = {
  classes: PropTypes.object,
  languages: PropTypes.array,
  orgSettings: PropTypes.object,
  knowledgeGraph: PropTypes.object,
  updateKnowledgeGraph: PropTypes.func,
  deleteKnowledgeGraph: PropTypes.func,
  fetchKnowledgeGraphs: PropTypes.func,
  createDevKnowledgeGraph: PropTypes.func
};
